import type { SupabaseClient } from '@supabase/supabase-js';
import {
  CUSTOMER_AUTH_MODELS,
  DEFAULT_CUSTOMER_AUTH_MODEL,
  type CustomerAuthModel,
} from '@/types/organization';

/**
 * Per-org customer authentication model helper (#652).
 *
 * Reads `settings.customer_auth_model` from the JSONB `organizations.settings`
 * blob. A missing or unrecognised value resolves to `passwordless`, so orgs
 * that have never been configured keep today's sign-in journey.
 */

export function isCustomerAuthModel(value: unknown): value is CustomerAuthModel {
  return (
    typeof value === 'string' &&
    (CUSTOMER_AUTH_MODELS as readonly string[]).includes(value)
  );
}

/** Steps of a `passwordless_plus_factor` sign-in; either may come first. */
export const PASSWORDLESS_PLUS_FACTOR_STEPS = ['email', 'sms'];

/** Read the customer authentication model from an `organizations.settings` blob. */
export function getCustomerAuthModel(settings: unknown): CustomerAuthModel {
  if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
    return DEFAULT_CUSTOMER_AUTH_MODEL;
  }
  const raw = (settings as Record<string, unknown>).customer_auth_model;
  return isCustomerAuthModel(raw) ? raw : DEFAULT_CUSTOMER_AUTH_MODEL;
}

/** Refusal code when a model 2 customer tries to sign in without a password. */
export const PASSWORD_REQUIRED_CODE = 'password_required';
export const PASSWORD_REQUIRED_MESSAGE = 'Please sign in with your username and password.';
/** Shown when a model 2 invitee proves their contact before creating a password. */
export const CREATE_PASSWORD_MESSAGE = 'Create a password to finish setting up your account.';

/** Refusal code when a model 3 organisation is given only one contact (§9). */
export const BOTH_CONTACTS_REQUIRED_CODE = 'both_contacts_required';

export type CustomerContactField = 'email' | 'phone';

/** True when the model signs customers in with both an email address and a mobile number. */
export function requiresBothContacts(model: CustomerAuthModel): boolean {
  return model === 'passwordless_plus_factor';
}

const BOTH_CONTACTS_REASON =
  'This organisation signs customers in with both an email address and a mobile number.';

/** The first missing contact when the model needs both, otherwise null. */
export function findMissingRequiredContact(
  model: CustomerAuthModel,
  contacts: { email?: string | null; phone?: string | null },
): { field: CustomerContactField; error: string } | null {
  if (!requiresBothContacts(model)) return null;
  if (!contacts.email?.trim()) {
    return { field: 'email', error: `Email address is required. ${BOTH_CONTACTS_REASON}` };
  }
  if (!contacts.phone?.trim()) {
    return { field: 'phone', error: `Mobile number is required. ${BOTH_CONTACTS_REASON}` };
  }
  return null;
}

/** True when every model allows a session from a single factor. */
export function allowsSingleFactorSession(models: readonly CustomerAuthModel[]): boolean {
  return models.every((model) => model === 'passwordless');
}

/**
 * Models of every organisation the account is a customer of, read from its
 * referrals and invitations (the same ownership `find_user_by_contact` uses).
 * Service-role client only. Throws on a read error so callers fail closed.
 */
export async function getUserCustomerAuthModels(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  admin: SupabaseClient<any>,
  userId: string,
): Promise<CustomerAuthModel[]> {
  const [referrals, invitations] = await Promise.all([
    admin.from('customer_referrals').select('organization_id').eq('customer_id', userId),
    admin.from('referral_invitations').select('organization_id').eq('customer_id', userId),
  ]);
  if (referrals.error) throw referrals.error;
  if (invitations.error) throw invitations.error;
  const orgIds = Array.from(
    new Set(
      [...(referrals.data ?? []), ...(invitations.data ?? [])]
        .map((row) => (row as { organization_id: string | null }).organization_id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0),
    ),
  );
  if (orgIds.length === 0) return [];
  const { data: orgs, error } = await admin
    .from('organizations')
    .select('settings')
    .in('id', orgIds);
  if (error) throw error;
  return (orgs ?? []).map((org) => getCustomerAuthModel((org as { settings: unknown }).settings));
}
